import React, { useState } from 'react';
import { Check, Palette } from 'lucide-react';
import { cn } from '@/modern/utils';
import { Button } from '@/modern/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent
  } from "@/modern/components/ui/popover";

import TaskEditorColorMapping from '@/other/constants/TaskEditorColors';

interface ModalColorPickerProps {
  value?: string | null;
  onChange: (color: string | null) => void;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Color picker component that uses Popover for modal compatibility
 * This ensures the color picker appears above modals with proper z-index
 */
export const ModalColorPicker: React.FC<ModalColorPickerProps> = ({
  value,
  onChange,
  clearable = true,
  disabled,
  className
}) => {
  const [open, setOpen] = useState(false);

  const handleColorSelect = (color: string | null) => {
    onChange(color);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className="h-swivu-input w-full justify-start gap-2"
        >
          <Palette className="h-4 w-4" style={{ color: value || "currentColor" }} />
          <span className="flex-1 text-left">
            {value ? "Color selected" : "Select color"}
          </span>
          {value && (
            <div
              className="h-4 w-4 rounded border border-border"
              style={{ backgroundColor: value }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-80 p-3 max-h-[400px] overflow-y-auto z-[--swivu-bryntum-z-index]', className)} align="start">
        <div className="space-y-3">
          {/* Color Grid */}
          <div className="grid grid-cols-10 gap-1">
            {TaskEditorColorMapping.map((colorItem, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  'h-7 w-7 rounded border border-border hover:scale-110 transition-transform relative',
                  value === colorItem.color && 'ring-2 ring-primary ring-offset-2'
                )}
                style={{ backgroundColor: colorItem.color }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleColorSelect(colorItem.color);
                }}
                title={colorItem.color}
              >
                {value === colorItem.color && (
                  <Check
                    className="absolute inset-0 m-auto h-4 w-4"
                    style={{ color: colorItem.text_color }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Clear Color Button */}
          {clearable && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleColorSelect(null);
              }}
            >
              <i className="fa-regular fa-eraser mr-2" />
              Remove color
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
